import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';

import { ConfigService } from '../data-services/config.abstract.data-service';
import { SettingsService } from '../data-services/settings.data-service';
import { Setting } from '../entities/setting.entity';
import { defaultSettingsConfig, SettingsConfig } from '../entities/settings-config.entity';

@Injectable({
    providedIn: 'root'
})
export class SettingsConfigService extends ConfigService<SettingsConfig> {
    protected readonly FALLBACK_VALUE: SettingsConfig = defaultSettingsConfig;
    protected readonly TYPE_NAME: string = 'SettingsConfig';

    protected readonly settingKeyMapping: Record<keyof SettingsConfig, string> = {
        parentSite: 'mh2_parent_site',
        parentSiteLabel: 'mh2_parent_site_label',
        zoneId: 'mh2_concepts_ingestspace',
        adminEmail: 'mh2_email',
        tracking: 'mh2_tracking',
        enableAiKeywordSuggestions: 'mh2_enable_ai_keyword_suggestions',
        defaultMapView: 'mh2_default_map_view',
        banner: 'mh2_banner',
        disclaimerConfig: 'mh2_disclaimer',
        siteIsPrivate: 'site_is_private',
        portalUploadProjectUrl: 'mh2_portal_upload_project',
        enableRegistration: 'user_register_link',
        isDefaultApp: 'mh2_default_app_is_portal',
        deleteRecycleDelay: 'permanent_delete_grace_period',
        enablePimFeatures: 'mh2_enable_pim_features',
        imageFitMode: 'mh2_image_fit_mode',
        readExportMode: 'mh2_read_export_mode',
        maintenanceNotification: 'mh2_maintenance_notification'
    };

    constructor(private settingsService: SettingsService) {
        super();
    }

    public initialize(): Observable<SettingsConfig> {
        const settings$ = this.settingsService.getAll();

        this.config$ = settings$.pipe(
            map(
                settings =>
                    Object.keys(this.settingKeyMapping).reduce((currentPartialConfig, configKey) => {
                        return {
                            ...currentPartialConfig,
                            [configKey]: this.findSetting(settings, configKey as keyof SettingsConfig)
                        };
                    }, {}) as unknown as SettingsConfig
            ),
            shareReplay(1)
        );

        this.initConfigListener();

        return this.config$.pipe(take(1));
    }

    private findSetting(settings: Setting[], configKey: keyof SettingsConfig): any {
        const settingKey: string = this.settingKeyMapping[configKey];
        const setting: Setting | undefined = settings.find(setting => setting.Key === settingKey);

        if (setting === undefined) {
            console.warn(`SettingsConfig:: Setting ${settingKey} not found. Falling back to default value.`);
            return this.FALLBACK_VALUE[configKey];
        }

        // If setting defined, but value is null, also return default
        return setting.Value ?? this.FALLBACK_VALUE[configKey];
    }
}
